import { useState } from 'preact/hooks';
import { useLocation } from 'wouter-preact';

/**
 * Login page — email + password form. Posts to /api/auth/login which
 * sets an HttpOnly session cookie on success. After auth, the app
 * navigates back to the originally-requested route (or /mission).
 *
 * Dwight rule 2026-05-14: no more token-in-URL bookmarks; users log in
 * once per browser with credentials and stay signed in via cookie.
 */
export function Login() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [, setLocation] = useLocation();

  async function submit(e: Event) {
    e.preventDefault();
    if (!email.trim() || !password) return;
    setSubmitting(true);
    setError(null);
    try {
      const res = await fetch('/api/auth/login', {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ email: email.trim(), password }),
      });
      const body = await res.json().catch(() => ({}));
      if (!res.ok || !body?.ok) {
        setError(body?.error || `Login failed (${res.status})`);
        return;
      }
      // Where to land? If the URL has ?next=… honour it; otherwise default route
      const next = new URLSearchParams(window.location.search).get('next');
      if (body.mustChangePassword) {
        setLocation('/change-password' + (next ? `?next=${encodeURIComponent(next)}` : ''));
      } else {
        setLocation(next && next.startsWith('/') ? next : '/mission');
      }
    } catch (err: any) {
      setError(err?.message || 'Network error');
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div class="min-h-screen min-h-[100dvh] flex items-center justify-center bg-[var(--color-bg)] text-[var(--color-text)] px-4">
      <div class="w-full max-w-sm">
        <div class="mb-8 text-center">
          <div class="text-2xl font-semibold mb-1">ClaudeClaw</div>
          <div class="text-sm text-[var(--color-text-muted)]">Mission Control · The Land Mule</div>
        </div>
        <form
          onSubmit={submit}
          class="bg-[var(--color-card)] border border-[var(--color-border)] rounded-lg p-5 space-y-4"
        >
          <div>
            <label class="block text-[11px] uppercase tracking-wider text-[var(--color-text-muted)] mb-1">Email</label>
            <input
              type="email"
              required
              autocomplete="email"
              value={email}
              onInput={(e) => setEmail((e.target as HTMLInputElement).value)}
              class="w-full bg-[var(--color-elevated)] border border-[var(--color-border)] rounded px-3 py-2 text-[13px] outline-none focus:border-[var(--color-accent)]"
              placeholder="you@thelandmule.com"
              disabled={submitting}
            />
          </div>
          <div>
            <label class="block text-[11px] uppercase tracking-wider text-[var(--color-text-muted)] mb-1">Password</label>
            <input
              type="password"
              required
              autocomplete="current-password"
              value={password}
              onInput={(e) => setPassword((e.target as HTMLInputElement).value)}
              class="w-full bg-[var(--color-elevated)] border border-[var(--color-border)] rounded px-3 py-2 text-[13px] outline-none focus:border-[var(--color-accent)]"
              disabled={submitting}
            />
          </div>
          {error && (
            <div class="text-[12px] text-[var(--color-status-failed)] bg-[var(--color-status-failed)]/10 border border-[var(--color-status-failed)]/30 rounded px-3 py-2">
              {error}
            </div>
          )}
          <button
            type="submit"
            disabled={submitting || !email.trim() || !password}
            class="w-full bg-[var(--color-accent)] hover:bg-[var(--color-accent-hover)] disabled:opacity-40 disabled:cursor-not-allowed text-white rounded px-3 py-2 text-[13px] font-medium transition-colors"
          >
            {submitting ? 'Signing in…' : 'Sign in'}
          </button>
        </form>
        <div class="mt-4 text-center text-[11px] text-[var(--color-text-faint)]">
          Forgot your password? Ask Dwight to reset it.
        </div>
      </div>
    </div>
  );
}
